import { Alert, Button, Space, Spin, Tag, Typography } from 'antd';
import { ArrowLeftOutlined, PrinterOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { helpGuideService } from '@/services/helpGuide.service';
import { HowToGuideCard } from '@/components/help/HowToGuideCard';
import { useOptionalAiAgent } from '@/contexts/AiAgentContext';
import { runPrintWithDocumentTitle } from '@/utils/printDocumentTitle';

const { Title, Text } = Typography;

/** One how-to guide, tailored to the signed-in user; printable (browser print / save as PDF). */
export default function HelpGuideDetailPage() {
  const { guideId = '' } = useParams<{ guideId: string }>();
  const navigate = useNavigate();
  const ai = useOptionalAiAgent();

  const { data, isLoading, error } = useQuery({
    queryKey: ['help-guide', guideId],
    queryFn: () => helpGuideService.get(guideId),
    enabled: Boolean(guideId),
    staleTime: 60_000,
  });
  const guide = data?.guide;

  return (
    <div style={{ maxWidth: 960, margin: '0 auto' }}>
      <Space className="no-print" wrap style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }}>
        <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/help')}>
          Help Center
        </Button>
        <Space wrap>
          {ai && guide ? (
            <Button icon={<QuestionCircleOutlined />} onClick={() => ai.askAssistant(`How do I ${guide.title.toLowerCase()}?`)}>
              Ask the assistant
            </Button>
          ) : null}
          <Button
            icon={<PrinterOutlined />}
            disabled={!guide}
            onClick={() => runPrintWithDocumentTitle(guide ? `How-to - ${guide.title}` : null)}
          >
            Print / PDF
          </Button>
        </Space>
      </Space>

      {error ? (
        <Alert type="error" showIcon message={error instanceof Error ? error.message : 'Guide not found'} />
      ) : null}

      <Spin spinning={isLoading}>
        {guide ? (
          <>
            <Space direction="vertical" size={4} style={{ width: '100%', marginBottom: 12 }}>
              <Space size={8} wrap>
                <Title level={3} style={{ margin: 0 }}>
                  {guide.title}
                </Title>
                <Tag>{guide.moduleLabel}</Tag>
              </Space>
              {guide.summary ? <Text type="secondary">{guide.summary}</Text> : null}
            </Space>
            <HowToGuideCard
              payload={guide}
              page
              onRelated={(id) => navigate(`/help/${encodeURIComponent(id)}`)}
            />
            {data?.phrases?.length ? (
              <Text type="secondary" className="no-print" style={{ display: 'block', marginTop: 12, fontSize: 12 }}>
                Also answers: {data.phrases.slice(0, 6).join(' · ')}
              </Text>
            ) : null}
          </>
        ) : null}
      </Spin>
    </div>
  );
}
